import { call, put, takeLatest, select } from 'redux-saga/effects';
import moment from 'moment';
import * as authService from '../../services/authService';
import { loginAction, logoutAction, doctorLoginAction, resetPasswordAction,doctorlogoutAction, organizationLoginAction, organizationlogoutAction, subadminlogoutAction, SubadminLoginAction, resetOrgPasswordAction, resetSubadminPasswordAction, verifyOtpAction, resendOtpAction, activationLoginAction, preorderOtpLoginAction, } from './authActions';
import { loginSuccess, loginFailure, logout ,doctorLogout, organizationlogout, subadminlogout, setOtpSent,showDeleteRequestModal, setExpiredPendingAppointments, setPendingAppointmentsModal} from './authSlice';
import { push } from '../router/routerSlice';
import { setReturnTo, takeReturnTo } from '../../utils/returnTo';
import { markJustPlaced } from '../../pages/Preorder/utils';
import { doctorloginApi, organizationloginApi } from '../../services/authService';
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.min.css';
import store from '../store';
import { getDoctorAppointmentApi } from '../../services/mainService';

// Common toast configuration
const TOAST_CONFIG = {
  position: "bottom-center",
  closeButton: false,
  style: { width: 'auto', borderRadius: 8 }
};


function* loginSaga(action) {
  try {
    let userType = sessionStorage.getItem("userType");

    if (!userType) {
      userType = localStorage.getItem("userType");
    }

    if (!userType) {
      setTimeout(() => {
        store.dispatch({ type: "auth/forceLogoutRequest" });
      }, 3000);
    }

    const apiResponse = yield call(authService.loginApi, action.payload);

    // Handle empty response (patient-specific check)
    if (userType === "patient" && (!apiResponse || Object.values(apiResponse).length === 0)) {
      return;
    }

    // 2FA-exempt accounts (backend TWO_FA_EXEMPT_EMAILS) get tokens straight
    // from sign-in with is2fa: false, so finish the login without the OTP step.
    const loginData = apiResponse.response_type === "success" ? apiResponse.response?.data : null;
    if (loginData && loginData.is2fa === false && loginData.token) {
      yield call(completeLoginSaga, apiResponse, userType);
      return;
    }

    if (apiResponse.response_type === "success") {
      // Set OTP sent status to trigger OTP UI
      yield put(setOtpSent(true));

      toast.success("OTP sent to your email address", {
        ...TOAST_CONFIG,
        autoClose: 3000
      });
    } else {
       const errorMessage = apiResponse.response?.message || "Invalid credentials";
  yield put(loginFailure(errorMessage));
  toast.error(errorMessage, { ...TOAST_CONFIG });
    }
  } catch (error) {
    console.log(`${sessionStorage.getItem("userType") || "User"} Login Error:`, error);
    yield put(loginFailure('Something went wrong'));
  }
}

// Stores the session and routes the user once login is complete, either after
// OTP verification or directly from sign-in for 2FA-exempt accounts.
function* completeLoginSaga(apiResponse, userType) {
  const userData = apiResponse.response.data;
  userData.userType = userType;

  yield put(loginSuccess(userData));

  if (userType === 'doctor') {
    yield put({
      type: 'doctor/checkExpiredPendingAppointments',
      payload: { token: userData.token }
    });
  }

  // Handle delete request modal (patient-specific)
  if (userType === "patient" && userData.isDeleteRequested === true) {
    yield put(showDeleteRequestModal(apiResponse?.response?.message));
  }

  yield put(setOtpSent(false)); // Reset OTP sent status

  // Handle routing based on user type
  yield call(handleUserTypeRouting, userData, userType);
}

/**
 * Confirms a pre-order signup from the emailed code.
 *
 * Finishes through `completeLoginSaga` like every other sign-in, so the stored
 * session and the post-login routing are identical - including the returnTo
 * that carries them on to their pre-orders.
 */
function* preorderOtpLoginSaga(action) {
  try {
    sessionStorage.setItem("userType", "patient");
    localStorage.setItem("userType", "patient");

    const apiResponse = yield call(authService.verifyPreorderOtpApi, action.payload);
    if (apiResponse?.response_type !== "success") {
      yield put(loginFailure(apiResponse?.response?.message || 'Could not confirm that code'));
      return;
    }

    /*
     * Where they land depends on what the server actually did. Confirming the
     * code usually places the order held against the signup - in which case
     * the pre-order list is the right destination and the confirmation is
     * worth showing. If placing it failed (sold out, a cap reached) there is
     * nothing to announce and nothing to list, so they go back to the product
     * to see why.
     */
    const placed = apiResponse.response?.data?.preorder;
    if (placed) {
      markJustPlaced(placed.orderNumber);
      setReturnTo('/patient/preorders');
    } else if (apiResponse.response?.data?.preorderSlug) {
      // The server tells us which product it was; the request body cannot
      // carry it, because the endpoint validates against a closed schema.
      setReturnTo(`/preorder/${apiResponse.response.data.preorderSlug}`);
    }

    yield call(completeLoginSaga, apiResponse, 'patient');
  } catch (error) {
    yield put(loginFailure(error.message));
  }
}

/**
 * Signs a patient in from the one-time code in their activation email.
 *
 * Deliberately finishes through `completeLoginSaga`, the same path a password
 * login takes, so the stored session, the delete-request handling and the
 * post-login routing are identical - including the returnTo that carries them
 * on to their pre-order.
 */
function* activationLoginSaga(action) {
  try {
    // The API urls resolve the account type from storage.
    sessionStorage.setItem("userType", "patient");
    localStorage.setItem("userType", "patient");

    const apiResponse = yield call(authService.activationSessionApi, action.payload);
    if (apiResponse?.response_type !== "success") {
      yield put(loginFailure(apiResponse?.response?.message || 'Could not sign you in'));
      return;
    }
    yield call(completeLoginSaga, apiResponse, 'patient');
  } catch (error) {
    yield put(loginFailure(error.message));
  }
}

// ==========================================
// UNIFIED OTP VERIFICATION SAGA - Handles all user types
// ==========================================
function* verifyOtpSaga(action) {
  try {
     let userType = sessionStorage.getItem("userType");

    if (!userType) {
      userType = localStorage.getItem("userType");
    }

    if (!userType) {
      setTimeout(() => {
        store.dispatch({ type: "auth/forceLogoutRequest" });
      }, 3000);
    }

    const apiResponse = yield call(authService.verifyLoginOtpApi, action.payload);

    // Handle empty response
    if (!apiResponse || Object.values(apiResponse).length === 0) {
      yield put(loginFailure('Something went wrong'));
      return;
    }

    if (apiResponse.response_type === "success") {
      yield call(completeLoginSaga, apiResponse, userType);
    } else {
      const errorMessage = apiResponse.response.message;
      toast.error(errorMessage, {
        ...TOAST_CONFIG,
        autoClose: 1000
      });
      yield put(loginFailure(errorMessage));
    }
  } catch (error) {
    console.log('OTP Verification Error:', error);
    toast.error('OTP verification failed', {
      ...TOAST_CONFIG,
      autoClose: 1000
    });
    yield put(loginFailure('OTP verification failed'));
  }
}

// ==========================================
// UNIFIED RESEND OTP SAGA - Handles all user types
// ==========================================
function* resendOtpSaga(action) {
  try {
    const apiResponse = yield call(authService.resendLoginOtpApi, action.payload);

    if (apiResponse.response_type === "success") {
      toast.success("OTP resent successfully", {
        ...TOAST_CONFIG,
        autoClose: 2000
      });
    } else {
      const errorMessage = apiResponse?.response?.message || 'Failed to resend OTP';
      toast.error(errorMessage, {
        ...TOAST_CONFIG,
        autoClose: 1000
      });
    }
  } catch (error) {
    console.log('Resend OTP Error:', error);
    toast.error('Failed to resend OTP', {
      ...TOAST_CONFIG,
      autoClose: 1000
    });
  }
}

// Helper function to handle routing based on user type
// Exported so the post-login destination can be asserted directly in a test;
// nothing else imports it.
export function* handleUserTypeRouting(userData, userType) {
  toast.success("Login successful", {
    position: "bottom-center",
    closeButton: false,
    autoClose: 1000,
    style: { width: 'auto', borderRadius: 8 }
  });

  switch (userType) {
    case 'patient': {
      // A patient who was part-way through something when they were asked to
      // log in goes back to it. Checked before the onboarding redirects
      // below, because a brand-new account fails both of them: the signup
      // form collects no gender, and an account created with an email starts
      // unverified. Routed by those rules first, someone who just signed up
      // to place a pre-order would never arrive at their own checkout.
      //
      // Only the pre-order flow ever sets this, so every other patient login
      // is routed exactly as before. The onboarding prompts are redirects
      // rather than gates - nothing stops the patient reaching those pages
      // afterwards - and the pre-order pages need only a valid token.
      const returnTo = takeReturnTo();
      if (returnTo) {
        yield put(push(returnTo));
      } else if (userData?.termscondition === false) {
        yield put(push("/first-time-login"));
      } else if (userData?.verified === true) {
        if (userData.gender === null) {
          yield put(push("/patient/settings"));
        } else {
          yield put(push("/patient/dashboard"));
        }
      } else {
        yield put(push('/patient/medical-record'));
      }
      break;
    }

    case 'doctor':
      yield put(push("/doctor/schedule"));
      break;

    case 'organization':
      yield put(push("/organization/dashboard"));
      break;

    case 'subadmin':
      yield put(push("/subadmin/home"));
      break;

    default:
      yield put(push("/"));
  }
}


function* doctorLoginSaga(action) {
  try {
    const apiResponse = yield call(doctorloginApi, action.payload);
    if (apiResponse.response_type === "success") {
      // Instead of logging in, trigger the OTP UI
      yield put(setOtpSent(true));
      toast.success("OTP sent to your email address", {
        position: "bottom-center",
        autoClose: 3000,
        // ... other toast styles
      });
    } else {
      yield put(loginFailure(apiResponse.response.message));
    }
  } catch (error) {
    console.log('Error:', error);
    yield put(loginFailure('Something went wrong'));
  }
}

function* organizationLoginSaga(action) {
  try {
    //const apiResponse = yield call(authService.doctorloginApi, action.payload);
    const apiResponse = yield call(authService.organizationloginApi, action.payload);
    if (Object.values(apiResponse).length === 0) {
      // yield put({ type: authActionTypes.LOG_OUT.SUCCESS, payload: {} });
    } else {
      if (apiResponse.response_type === "success") {
        const userData = apiResponse.response.data;
        userData.userType = "organization"
        yield put(loginSuccess(userData));
        toast.success(apiResponse?.response?.message, { position: "bottom-center", closeButton: false , autoClose: 1000, style: {width: 'auto', borderRadius: 8 }});
        yield put(push("/organization/dashboard"));
      } else {
        toast.error(apiResponse.response.message, { position: "bottom-center", closeButton: false , autoClose: 1000, style: {width: 'auto', borderRadius: 8 }});
        yield put(loginFailure(apiResponse.response.message))
      }
    }
  }
  catch (error) {
    console.log('Error:', error);
  }
}
function* subadminLoginSaga(action) {
  try {
    
    const apiResponse = yield call(authService.subadminloginApi, action.payload);
    if (Object.values(apiResponse).length === 0) {
      // yield put({ type: authActionTypes.LOG_OUT.SUCCESS, payload: {} });
    } else {
      if (apiResponse.response_type === "success") {
        const userData = apiResponse.response.data;
        console.log("userData",userData)
        userData.userType = "subadmin"
        yield put(loginSuccess(userData));
        toast.success(apiResponse?.response?.message, { position: "bottom-center", closeButton: false , autoClose: 1000, style: {width: 'auto', borderRadius: 8 }});
        yield put(push("/subadmin/home"));
      } else {
        toast.error(apiResponse.response.message, { position: "bottom-center", closeButton: false , autoClose: 1000, style: {width: 'auto', borderRadius: 8 }});
        yield put(loginFailure(apiResponse.response.message))
      }
    }
  }
  catch (error) {
    console.log('Error:', error);
  }
}

function* resePasswordSaga(action) {
  const response = yield call(authService.updateProfile, action.payload.credentials, action.payload.token);
  if (response.response_type === 'success') {   
    yield put(logout);  
    localStorage.clear();
    sessionStorage.clear();
    toast.success("Password updated successfully.", { position: "bottom-center", closeButton: false, autoClose: 1000, style: {width: 'auto', borderRadius: 8 }});
    yield put(push("/"));
  } else {
    toast.error(response?.response?.message, { position: "bottom-center", closeButton: false, autoClose: 1000, style: {width: 'auto', borderRadius: 8 }});
  }
}


function* logoutSaga(action) {
   const refreshToken = yield select((state) => state.auth.refreshToken);
  const response = yield call(authService.logout, action.payload, refreshToken);
  if (response.response_type === 'success') {   
    yield put(logout);  
    localStorage.clear();
    sessionStorage.clear();
    toast.success(response?.response?.message, { position: "bottom-center", closeButton: false, autoClose: 1000, style: {width: 'auto', borderRadius: 8 }});
    yield put(push("/"));
  } else {
    toast.error(response?.response?.message, { position: "bottom-center", closeButton: false, autoClose: 1000, style: {width: 'auto', borderRadius: 8 }});
  }
}

function* forceLogoutSaga() {
   // No API call here
  yield put({ type: "auth/logout" }); // This updates Redux state
  localStorage.clear();
  sessionStorage.clear();
  
  // Redirect first
  yield put(push("/"));

}

function* doctorlogoutSaga(action) {
   const refreshToken = yield select((state) => state.auth.refreshToken);
  const response = yield call(authService.doctorlogout, action.payload, refreshToken);
  if (response.response_type === 'success') {   
    yield put(doctorLogout);
    localStorage.clear();
    sessionStorage.clear();
    toast.success(response?.response?.message, { position: "bottom-center", closeButton: false, autoClose: 1000, style: {width: 'auto', borderRadius: 8 }});
    yield put(push("/"));
  } else {
    toast.error(response?.response?.message, { position: "bottom-center", closeButton: false, autoClose: 1000, style: {width: 'auto', borderRadius: 8 }});
  }
}

function* organizationlogoutSaga(action) {
    const refreshToken = yield select((state) => state.auth.refreshToken);
  const response = yield call(authService.organizationlogout, action.payload,refreshToken);
  if (response.response_type === 'success') {   
    yield put(organizationlogout);
    localStorage.clear();
    sessionStorage.clear();
    toast.success(response?.response?.message, { position: "bottom-center", closeButton: false, autoClose: 1000, style: {width: 'auto', borderRadius: 8 }});
    yield put(push("/"));
  } else {
    toast.error(response?.response?.message, { position: "bottom-center", closeButton: false, autoClose: 1000, style: {width: 'auto', borderRadius: 8 }});
  }
}

function* resetOrgPasswordSaga(action) {
  const response = yield call(authService.organizationResetprofile, action.payload.credentials, action.payload.token);
  if (response.response_type === 'success') {   
    yield put(organizationlogout);  
    localStorage.clear();
    sessionStorage.clear();
    toast.success("Password updated successfully.", { position: "bottom-center", closeButton: false, autoClose: 1000, style: {width: 'auto', borderRadius: 8 }});
    yield put(push("/"));
  } else {
    toast.error(response?.response?.message, { position: "bottom-center", closeButton: false, autoClose: 1000, style: {width: 'auto', borderRadius: 8 }});
  }
}
function* resetSubadminPasswordSaga(action) {
  const response = yield call(authService.subadminResetprofile, action.payload.credentials, action.payload.token);
  if (response.response_type === 'success') {   
    yield put(subadminlogout);  
    localStorage.clear();
    sessionStorage.clear();
    toast.success("Password updated successfully.", { position: "bottom-center", closeButton: false, autoClose: 1000, style: {width: 'auto', borderRadius: 8 }});
    yield put(push("/"));
  } else {
    toast.error(response?.response?.message, { position: "bottom-center", closeButton: false, autoClose: 1000, style: {width: 'auto', borderRadius: 8 }});
  }
}
function* subadminlogoutSaga(action) {
  const refreshToken = yield select((state) => state.auth.refreshToken);
  console.log('refreshToken____refreshToken',refreshToken);
  const response = yield call(authService.subadminlogout, action.payload, refreshToken);
  console.log("auth",response)
  console.log("action",action)

  if (response.response_type === 'success') {   
    yield put(subadminlogout);
    localStorage.clear();
    sessionStorage.clear();
    toast.success(response?.response?.message, { position: "bottom-center", closeButton: false, autoClose: 1000, style: {width: 'auto', borderRadius: 8 }});
    yield put(push("/"));
  } else {
    toast.error(response?.response?.message, { position: "bottom-center", closeButton: false, autoClose: 1000, style: {width: 'auto', borderRadius: 8 }});
  }
}

function* checkExpiredPendingAppointmentsSaga(action) {
  try {
    const apiResponse = yield call(getDoctorAppointmentApi, {
      token: action.payload.token,
      status: 1  
    });

    if (
      !apiResponse || 
      Object.values(apiResponse).length === 0 ||
      apiResponse.response_type !== "success"
    ) {
      return;
    }

    const appointments = apiResponse.response.data || [];
    const now = moment();

    
    const expiredPending = appointments.filter(item => {
      const endTime = moment(item.end_time);
      return endTime.isBefore(now);
    });

    if (expiredPending.length > 0) {
      yield put(setExpiredPendingAppointments(expiredPending));  
      yield put(setPendingAppointmentsModal(true));              
    }

  } catch (error) {
    console.log('Expired pending appointments check error:', error);
  }
}



export function* authSaga() {
  yield takeLatest(loginAction.type, loginSaga);
  yield takeLatest(activationLoginAction.type, activationLoginSaga);
  yield takeLatest(preorderOtpLoginAction.type, preorderOtpLoginSaga);
  yield takeLatest(verifyOtpAction.type, verifyOtpSaga);
   yield takeLatest(resendOtpAction.type, resendOtpSaga);
  yield takeLatest(logoutAction.type, logoutSaga);
  yield takeLatest(doctorLoginAction.type, doctorLoginSaga);
  yield takeLatest(resetPasswordAction.type, resePasswordSaga);
  yield takeLatest(doctorlogoutAction.type, doctorlogoutSaga);
  yield takeLatest(organizationLoginAction.type,  organizationLoginSaga);
  yield takeLatest(organizationlogoutAction.type, organizationlogoutSaga);
  yield takeLatest(SubadminLoginAction.type, subadminLoginSaga);
  yield takeLatest(subadminlogoutAction.type, subadminlogoutSaga);
  yield takeLatest(resetOrgPasswordAction.type,resetOrgPasswordSaga);
  yield takeLatest(resetSubadminPasswordAction.type,resetSubadminPasswordSaga);
  yield takeLatest(resetSubadminPasswordAction.type,resetSubadminPasswordSaga);
  yield takeLatest("auth/forceLogoutRequest", forceLogoutSaga); 
yield takeLatest('doctor/checkExpiredPendingAppointments', checkExpiredPendingAppointmentsSaga);
}
